/* Meru House — Category Posters (sections/mh-poster-accordion.liquid)
   Custom element: survives theme-editor section reloads through
   connectedCallback / disconnectedCallback. The autoplay timer is the CSS
   progress-line animation: `animationend` advances the slide, so pausing is
   just `animation-play-state: paused` (class .is-paused). */
(function () {
  if (customElements.get('mh-poster-accordion')) return;

  var mqDesktop = window.matchMedia('(min-width: 990px)');
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqFine = window.matchMedia('(hover: hover) and (pointer: fine)');
  var HEADER_OFFSET = 80;

  class MhPosterAccordion extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.panels = Array.prototype.slice.call(this.querySelectorAll('.mhpa__panel'));
      this.track = this.querySelector('.mhpa__track');
      if (!this.panels.length || !this.track) return;
      this._ready = true;

      this.btn = this.querySelector('.mhpa__pause');
      this.index = Math.max(0, this.panels.findIndex(function (p) { return p.classList.contains('is-active'); }));
      this.panels.forEach(function (p, i) { p.classList.toggle('is-active', i === this.index); }, this);

      this.flags = { user: false, hover: false, focus: false, offscreen: true, hidden: document.hidden, editor: false, touched: false };
      this.autoplay = this.dataset.autoplay === 'true' && !mqReduce.matches && this.panels.length > 1;
      this.autoplayMobile = this.dataset.autoplayMobile === 'true';
      this._lastAuto = 0;

      this._ac = new AbortController();
      var o = { signal: this._ac.signal };
      var self = this;

      this.panels.forEach(function (panel, i) {
        var link = panel.querySelector('.mhpa__link');
        panel.addEventListener('mouseenter', function () {
          if (!mqFine.matches) return;
          clearTimeout(self._hoverT);
          self._hoverT = setTimeout(function () { self.activate(i); }, 90);
        }, o);
        panel.addEventListener('mouseleave', function () { clearTimeout(self._hoverT); }, o);
        if (!link) return;
        // Keyboard only: a tap also focuses the link in some browsers, and that
        // must not open the panel before the click handler runs.
        link.addEventListener('focus', function () {
          if (link.matches(':focus-visible')) self.activate(i);
        }, o);
        link.addEventListener('click', function (e) {
          if (mqFine.matches && mqDesktop.matches) return; // desktop mouse: always follow the link
          // Touch: if autoplay moved the layout in the last moment, the finger may
          // have landed on a different panel than intended. Ignore that tap.
          if (performance.now() - self._lastAuto < 1000) { e.preventDefault(); return; }
          if (i === self.index) return;                    // open poster: follow the link
          e.preventDefault();                              // closed band: first tap opens it
          self.activate(i, { user: true });
        }, o);
      });

      this.addEventListener('mouseenter', function () { if (mqFine.matches) self.set('hover', true); }, o);
      this.addEventListener('mouseleave', function () { self.set('hover', false); }, o);
      this.addEventListener('focusin', function () { self.set('focus', true); }, o);
      this.addEventListener('focusout', function (e) {
        if (!self.contains(e.relatedTarget)) self.set('focus', false);
      }, o);
      // On touch screens the shopper has taken control: stop rotating for good.
      this.addEventListener('touchstart', function () { self.set('touched', true); }, { passive: true, signal: this._ac.signal });

      this.track.addEventListener('animationend', function (e) {
        if (e.animationName !== 'mhpa-progress') return;
        var panel = e.target.closest('.mhpa__panel');
        if (!panel || panel !== self.panels[self.index]) return;
        self._lastAuto = performance.now();
        self.activate((self.index + 1) % self.panels.length);
      }, o);

      if (this.btn) {
        this.btn.addEventListener('click', function () { self.set('user', !self.flags.user); }, o);
      }
      document.addEventListener('visibilitychange', function () { self.set('hidden', document.hidden); }, o);
      mqDesktop.addEventListener('change', function () { self.sync(); }, o);

      document.addEventListener('shopify:block:select', function (e) {
        if (!e.detail || e.detail.sectionId !== self.dataset.sectionId) return;
        var i = self.panels.findIndex(function (p) { return p.dataset.blockId === e.detail.blockId; });
        if (i > -1) self.activate(i);
        self.set('editor', true);
      }, o);
      document.addEventListener('shopify:block:deselect', function (e) {
        if (e.detail && e.detail.sectionId === self.dataset.sectionId) self.set('editor', false);
      }, o);

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(function (entries) {
          var en = entries[0];
          self.set('offscreen', !(en.isIntersecting && en.intersectionRatio >= 0.35));
        }, { threshold: [0, 0.35, 0.6] });
        this._io.observe(this);
      } else {
        this.flags.offscreen = false;
      }

      this.sync();
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      if (this._io) this._io.disconnect();
      clearTimeout(this._hoverT);
      this._ready = false;
    }

    set(key, value) {
      if (this.flags[key] === value) return;
      this.flags[key] = value;
      this.sync();
    }

    sync() {
      var f = this.flags;
      // Stacked (mobile/tablet) layout only rotates if the merchant opted in:
      // bands changing height under a thumb cause mis-taps.
      var on = this.autoplay && (mqDesktop.matches || this.autoplayMobile);
      this.classList.toggle('is-autoplay', on);
      var paused = !on || f.user || f.hover || f.focus || f.offscreen || f.hidden || f.editor ||
        (f.touched && !mqDesktop.matches);
      this.classList.toggle('is-paused', paused);
      if (this.btn) {
        this.btn.hidden = !on || (f.touched && !mqDesktop.matches);
        this.btn.setAttribute('aria-pressed', String(f.user));
        this.btn.setAttribute('aria-label', f.user ? 'Play category slideshow' : 'Pause category slideshow');
      }
    }

    activate(i, opts) {
      opts = opts || {};
      if (i === this.index || !this.panels[i]) return;
      this.panels[this.index].classList.remove('is-active');
      var next = this.panels[i];
      next.classList.add('is-active');
      this.index = i;

      // Mobile: the band the shopper tapped moves when the open poster above it
      // closes. Once the motion ends, bring it back into view below the sticky header.
      if (opts.user && !mqDesktop.matches) {
        setTimeout(function () {
          var r = next.getBoundingClientRect();
          var dy = 0;
          if (r.top < HEADER_OFFSET) dy = r.top - HEADER_OFFSET;
          else if (r.bottom > window.innerHeight) dy = Math.min(r.bottom - window.innerHeight + 16, r.top - HEADER_OFFSET);
          if (dy) window.scrollBy({ top: dy, behavior: mqReduce.matches ? 'auto' : 'smooth' });
        }, mqReduce.matches ? 0 : 950);
      }
    }
  }

  customElements.define('mh-poster-accordion', MhPosterAccordion);
})();
